import React, { useState } from "react";
import EditImg from "../../../images/SideBar/Edit";
import Loader from "../../../components/Loader";
import CommonDataGrid from "../../../components/CommonDataGrid";
import Button from "../../../components/Botton";
import { Toaster } from "react-hot-toast";
import { GridColDef } from "@mui/x-data-grid";
import { TextField } from "@mui/material";
import DeleteImg from "../../../images/SideBar/Delete";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import useUser from "../useUser";
import EditUserIndex from "./EditUser";
import {
  DisabledByDefault,
  DisabledByDefaultOutlined,
  PersonOff,
  PersonOffOutlined,
  Block,
  BlockOutlined,
  ToggleOff,
  ToggleOn,
  Lock,
  LockOpen,
  VisibilityOff,
  Visibility
} from "@mui/icons-material";

const User: React.FC = () => {
  const {
    selectedUserId,
    userData,
    loading,
    searchQuery,
    setSearchQuery,
    handleEdit,
    handleDelete,
    handleStatusUp,
    handleDateFilter,
    handleAdd,
    handleDisable
  } = useUser();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);
  const [userToDisable, setUserToDisable] = useState<string | null>(null);
  const [actionType, setActionType] = useState<'delete' | 'disable' | 'enable'>('delete');

  const openDeleteModal = (id: string) => {
    setUserToDelete(id);
    setActionType('delete');
    setIsModalOpen(true);
  };

  const openDisableModal = (id: string, currentStatus: boolean) => {
    setUserToDisable(id);
    setActionType(currentStatus ? 'enable' : 'disable');
    setIsModalOpen(true);
  };

  const confirmDelete = () => {
    if (userToDelete) {
      handleDelete(userToDelete);
      closeModal();
    }
  };

  const confirmDisable = async () => {
    if (userToDisable) {
      await handleDisable(userToDisable);
      closeModal();
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setUserToDelete(null);
    setUserToDisable(null);
    setActionType('delete');
  };

  const getModalMessage = () => {
    switch (actionType) {
      case 'disable':
        return "Are you sure you want to disable this user?";
      case 'enable':
        return "Are you sure you want to enable this user?";
      default:
        return "Are you sure you want to delete this user?";
    }
  };

  const getModalConfirmAction = () => {
    switch (actionType) {
      case 'disable':
      case 'enable':
        return confirmDisable;
      default:
        return confirmDelete;
    }
  };

  // Function to render the appropriate icon based on user status
  const renderStatusIcon = (row: any) => {
    const isDisabled = row.isDisabled || row.disabled || row.status === 'disabled';

    if (isDisabled) {
      return (
        <button
          onClick={() => openDisableModal(row._id, true)}
          className="text-red-500 hover:text-green-500 transition-colors duration-200"
          title="User is disabled - Click to enable"
        >
          <PersonOff />
        </button>
      );
    } else {
      return (
        <button
          onClick={() => openDisableModal(row._id, false)}
          className="text-green-500 hover:text-red-500 transition-colors duration-200"
          title="User is active - Click to disable"
        >
          <Visibility />
        </button>
      );
    }
  };

  // Alternative icon options - you can swap these in renderStatusIcon above
  const alternativeIcons = {
    // Option 1: Lock/Unlock icons
    disabled: <Lock className="text-red-500" />,
    enabled: <LockOpen className="text-green-500" />,

    // Option 2: Toggle icons
    disabled2: <ToggleOff className="text-red-500" />,
    enabled2: <ToggleOn className="text-green-500" />,

    // Option 3: Block icons
    disabled3: <Block className="text-red-500" />,
    enabled3: <BlockOutlined className="text-green-500" />,
  };

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      align: "center",
      headerAlign: "center",
      flex: 1,
    },
    {
      field: "mobileNo",
      headerName: "Mobile Number",
      align: "center",
      headerAlign: "center",
      flex: 1,
    },
    {
      field: "email",
      headerName: "Email",
      align: "center",
      headerAlign: "center",
      flex: 1.5,
    },
    {
      field: "city",
      headerName: "City",
      align: "center",
      headerAlign: "center",
      flex: 1,
    },
    {
      field: "courseName",
      headerName: "Course",
      align: "center",
      headerAlign: "center",
      flex: 1,
    },
    {
      field: "createdAt",
      headerName: "Date",
      align: "center",
      headerAlign: "center",
      flex: 1,
      valueGetter: (params: any) => {
        if (!params) return "";
        const date = new Date(params);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      }
    },
    {
      field: "status",
      headerName: "Status",
      align: "center",
      headerAlign: "center",
      flex: 0.5,
      renderCell: (params) => {

        console.log(params.row)
        const isDisabled = params.row.isDisabled
        return (
          <div className="flex justify-center items-center mt-3">
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium ${isDisabled
                ? 'bg-red-100 text-red-800'
                : 'bg-green-100 text-green-800'
                }`}
            >
              {isDisabled ? 'Disabled' : 'Active'}
            </span>
          </div>
        );
      },
    },
    {
      field: "Actions",
      headerName: "Actions",
      align: "center",
      headerAlign: "center",
      flex: 0.5,
      renderCell: (params) => (
        <div className="flex h-full items-center justify-center">
          {renderStatusIcon(params.row)}
        </div>
      ),
    },
  ];

  return (
    <div className="mx-auto p-4">
      {loading && <Loader />}
      {selectedUserId ? (
        <EditUserIndex userId={selectedUserId} />
      ) : (
        <div className="space-y-4 mt-4 bg-white p-4 rounded-b-lg shadow-md">
          <div className="flex justify-between items-center gap-4 mr-1 mb-4">
            <TextField
              fullWidth
              variant="outlined"
              label="Search"
              placeholder="Type to search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{ maxWidth: '400px' }}
            />
            {/* <Button
              onClick={handleAdd}
              className="py-1.5 px-9 rounded"
              children="Add User"
              type="button"
            /> */}
          </div>
          <CommonDataGrid
            id="user"
            rows={userData}
            columns={columns}
            loading={!userData}
            showConfirmation={false}
            confirmationMessage={""}
            onCancelDelete={function (): void {
              throw new Error("Function not implemented.");
            }}
            onConfirmDelete={function (): void {
              throw new Error("Function not implemented.");
            }}
            toolbarProps={{ toolbar: { showQuickFilter: true } }}
          />
        </div>
      )}

      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onConfirm={getModalConfirmAction()}
        message={getModalMessage()}
      />

      <Toaster />
    </div>
  );
};

export default User;